import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { usePrivy, useUser, useSessionSigners } from "@privy-io/react-auth";
import { SOLANA_NETWORK } from "../contracts/solana/config";
import {
  subscribeQuickPlay,
  quickPlayState,
  markDelegated,
  clearDelegated,
} from "./quickPlayStore";

const SERVER_SIGNER_ID = import.meta.env.VITE_PRIVY_SERVER_SIGNER_ID as string | undefined;
const POLICY_IDS_RAW = import.meta.env.VITE_PRIVY_SESSION_POLICY_IDS as string | undefined;

const POLICY_IDS = (POLICY_IDS_RAW ?? "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

/**
 * Privy v2 ("TEE") wallets are flagged by recoveryMethod === "privy-v2" — the
 * SAME check the SDK uses internally to route session-signer calls. The new
 * Solana Privy app provisions these, and `useDelegatedActions().delegateWallet`
 * does NOT support them: it throws "this app uses TEE execution" INSIDE a
 * `new Promise(async executor)`, where a throw never rejects the promise — so
 * the call hung forever and the button sat on "Authorizing…" with no error.
 * `useSessionSigners().addSessionSigners` is the API that supports BOTH
 * architectures (TEE → additional signer on the wallet, on-device → classic
 * delegation), throws like a normal async function, and needs no consent modal.
 */
const isTeeWallet = (account: { id?: string; recoveryMethod?: string } | undefined): boolean =>
  Boolean(account?.id) && account?.recoveryMethod === "privy-v2";

/** Never let a Privy call hang the UI: surface a timeout instead. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out — please retry`)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

/** Privy rejects re-adding a signer that's already on the wallet — that IS the
 *  enabled state (e.g. re-enabling after a fresh login), not a failure. */
const alreadyEnabled = (err: unknown): boolean =>
  /alread|duplicate|exist/i.test((err as Error | undefined)?.message ?? "");

export interface UseEnableSessionSigner {
  ready: boolean;
  enabling: boolean;
  enabled: boolean;
  error: string | null;
  enable: () => Promise<void>;
  revoke: () => Promise<void>;
}

export function useEnableSessionSigner(): UseEnableSessionSigner {
  const { user } = usePrivy();
  // refreshUser re-pulls the user (and the `delegated` flag on linkedAccounts)
  // after add/removeSessionSigners — Privy's own internal refresh has been
  // observed to lag/miss, which left `enabled` stale and the whole Quick Play
  // flow looking dead after a successful enable.
  const { refreshUser } = useUser();
  // Session-signer API — the ONLY delegation path that works on both Privy
  // wallet architectures (see isTeeWallet above). Headless: the explicit
  // "Enable Quick Play" click is the consent, same as the EVM flow always was.
  const { addSessionSigners, removeSessionSigners } = useSessionSigners();
  // Cross-instance optimistic state: the instance that ran delegateWallet is
  // NOT the one that sweeps (App daemon) or gates the game CTA, so the
  // "delegation succeeded" fact must be shared, not kept in local state.
  const quickPlay = useSyncExternalStore(subscribeQuickPlay, quickPlayState);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // The embedded wallet to delegate, for the ACTIVE chain. On Solana we MUST
  // match chainType 'solana' — selecting any 'privy' wallet (or the EVM
  // `user.wallet` fallback) makes Privy treat the delegation as EVM and pop the
  // MetaMask/Tron connect modal instead of delegating the Solana wallet.
  const embeddedAddress = useMemo(() => {
    const accounts = (user?.linkedAccounts ?? []) as any[];
    if (SOLANA_NETWORK) {
      const w = accounts.find(
        (a) => a.type === "wallet" && a.walletClientType === "privy" && a.chainType === "solana",
      ) as { address?: string } | undefined;
      return w?.address ?? null;
    }
    const w = accounts.find((a) => a.type === "wallet" && a.walletClientType === "privy") as
      | { address?: string }
      | undefined;
    return w?.address ?? user?.wallet?.address ?? null;
  }, [user]);

  const enabled = useMemo(() => {
    if (!user || !embeddedAddress) return false;
    const wallet = user.linkedAccounts?.find(
      (a: any) => a.type === "wallet" && a.address === embeddedAddress,
    ) as { delegated?: boolean } | undefined;
    if (wallet?.delegated) return true;
    // Optimistic fallback: the store flag is only set after addSessionSigners
    // succeeded (signer registered with Privy), so trust it even while the
    // refreshed user object is still catching up (or never catches up).
    return quickPlay.delegatedAddress === embeddedAddress;
  }, [user, embeddedAddress, quickPlay.delegatedAddress]);

  // Enable/revoke both SET the error state (for passive UIs like the gate
  // button) AND rethrow (so imperative callers — Settings' applyMode, the
  // quick-play daemon — see the failure instead of continuing as if it worked).
  const enable = useCallback(async () => {
    if (!embeddedAddress) {
      setError("no embedded wallet to delegate");
      throw new Error("no embedded wallet to delegate");
    }
    setPending(true);
    setError(null);
    try {
      if (SOLANA_NETWORK) {
        // Grant the app's server signer access to the Solana embedded wallet so
        // the backend Wallet API (privy().walletApi.solana) can sign quick-play
        // deposits/locks with no per-action popup. TEE (privy-v2) wallets take
        // the signer as an additional_signer and REQUIRE its id; on-device
        // wallets delegate to the app's authorization key and REQUIRE [].
        const account = (user?.linkedAccounts ?? []).find(
          (a: any) => a.type === "wallet" && a.address === embeddedAddress,
        ) as { id?: string; recoveryMethod?: string } | undefined;
        const tee = isTeeWallet(account);
        if (tee && !SERVER_SIGNER_ID) {
          throw new Error(
            "Quick Play signer not configured — set VITE_PRIVY_SERVER_SIGNER_ID to this app's authorization key ID",
          );
        }
        await withTimeout(
          addSessionSigners({
            address: embeddedAddress,
            signers: tee
              ? [
                  {
                    signerId: SERVER_SIGNER_ID as string,
                    policyIds: POLICY_IDS.length ? POLICY_IDS : undefined,
                  },
                ]
              : [],
          }),
          30_000,
          "enable quick play",
        );
        // Success ⇒ the signer is on the wallet. Flip the shared flag NOW so
        // every hook instance (gate, Settings, sweep daemon) reacts
        // immediately, then converge the real source of truth.
        markDelegated(embeddedAddress);
        await refreshUser().catch(() => {});
      } else {
        if (!SERVER_SIGNER_ID) {
          setError("VITE_PRIVY_SERVER_SIGNER_ID not set");
          throw new Error("VITE_PRIVY_SERVER_SIGNER_ID not set");
        }
        await addSessionSigners({
          address: embeddedAddress,
          signers: [
            { signerId: SERVER_SIGNER_ID, policyIds: POLICY_IDS.length ? POLICY_IDS : undefined },
          ],
        });
      }
    } catch (err: any) {
      // "Signer already on the wallet" = we ARE enabled (fresh login where the
      // user object lagged) — finish as success instead of erroring.
      if (SOLANA_NETWORK && alreadyEnabled(err)) {
        markDelegated(embeddedAddress);
        await refreshUser().catch(() => {});
        return;
      }
      const msg = err?.message ?? "failed to enable quick play";
      setError(msg);
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setPending(false);
    }
  }, [addSessionSigners, embeddedAddress, refreshUser, user]);

  const revoke = useCallback(async () => {
    if (!embeddedAddress) return;
    setPending(true);
    setError(null);
    try {
      // removeSessionSigners handles both architectures (TEE → clears the
      // wallet's additional signers, on-device → revokes the delegation).
      await withTimeout(
        removeSessionSigners({ address: embeddedAddress }),
        30_000,
        "revoke quick play",
      );
      if (SOLANA_NETWORK) {
        clearDelegated();
        await refreshUser().catch(() => {});
      }
    } catch (err: any) {
      const msg = err?.message ?? "failed to revoke quick play";
      setError(msg);
      throw err instanceof Error ? err : new Error(msg);
    } finally {
      setPending(false);
    }
  }, [embeddedAddress, removeSessionSigners, refreshUser]);

  return {
    // On Solana, readiness is just "we have a Solana embedded wallet". A TEE
    // wallet additionally needs VITE_PRIVY_SERVER_SIGNER_ID — checked in
    // enable() with a loud, actionable error rather than silently hiding the
    // button here (this is an operator misconfig, not a user state).
    ready: SOLANA_NETWORK ? !!embeddedAddress : !!SERVER_SIGNER_ID && !!embeddedAddress,
    enabling: pending,
    enabled,
    error,
    enable,
    revoke,
  };
}
